import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Sort,
  Page,
  Selection,
  Inject,
} from '@syncfusion/ej2-react-grids';

import { useAppSelector } from '../../../../../app/store';
import { ViewNoteDialog } from '../../dialogs';
import { useNoteViewDialog } from './hooks';
import { dateTemplate, textTemplate } from './templates';

export interface NotesGridProps {
  testId?: string;
}

/**
 * NotesGrid - Displays notes for a case
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for dialog state management
 * - Clean component structure
 */
export const NotesGrid: React.FC<NotesGridProps> = ({ testId }) => {
  const { currentCase } = useAppSelector((state) => state.caseManage);
  const notes = currentCase?.notes || [];

  // Sort notes with newest first
  const sortedNotes = useMemo(
    () =>
      [...notes].sort(
        (a, b) => new Date(b.addDate).getTime() - new Date(a.addDate).getTime()
      ),
    [notes]
  );

  // Use custom hook for dialog state
  const { selectedNote, showViewDialog, handleRowSelected, handleCloseViewDialog } =
    useNoteViewDialog();

  return (
    <Box sx={{ height: '400px', width: '800px' }}>
      <GridComponent
        dataSource={sortedNotes}
        allowSorting={true}
        allowPaging={true}
        pageSettings={{ pageSize: 10 }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelected}
        height="100%"
        data-testid={testId}
      >
        <ColumnsDirective>
          <ColumnDirective
            field="addDate"
            headerText="Date Added"
            width="180"
            template={dateTemplate}
          />
          <ColumnDirective field="addedBy" headerText="Added By" width="150" />
          <ColumnDirective field="text" headerText="Note Text" template={textTemplate} />
        </ColumnsDirective>
        <Inject services={[Sort, Page, Selection]} />
      </GridComponent>

      {/* ViewNoteDialog - Self-contained within the grid */}
      {showViewDialog && selectedNote && (
        <ViewNoteDialog
          open={showViewDialog}
          note={selectedNote}
          onClose={handleCloseViewDialog}
        />
      )}
    </Box>
  );
};

export default NotesGrid;
